import React, { useState } from 'react';
import { Gap, Modal, Text } from '@common/components';
import { useMutation } from '@common/hooks';
import type { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import { createMessage } from 'works/legacy/lib/message-helper';
import { datetime } from '@common/services/datetime';
import type { LoadBalancingWorkerTermType } from '@load-balancing/graphql/queries/load-balancing-worker';
import { SAVE_WORKER_PROFILE_CHANGE_MUTATION } from '@load-balancing/graphql/mutations/save-worker-profile-change';
import {
  buildSaveVariables,
  initialFormValues,
  isEndBeforeStart,
  isPast,
  laterChanges,
  type WorkerProfileFormValues,
} from './_schedule-worker-profile-update-form-service';

// State + persistence for the "Update Profile" form (WORK-6258), shared by both hosts (the
// in-session profile panel and the Team Review drawer). The host fetches the worker's terms
// and renders the Save/Cancel chrome; this hook owns the form state, client-side date
// validation, and the mutation (refetching the read views on success).
export function useScheduleWorkerProfileUpdateForm({
  workerId,
  terms,
  asOf,
  editingTermId = null,
  schedulingPeriodId = null,
  onSaved,
}: {
  workerId: string;
  terms: readonly LoadBalancingWorkerTermType[];
  asOf: string;
  editingTermId?: string | null;
  schedulingPeriodId?: string | null;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<WorkerProfileFormValues>(() =>
    initialFormValues({ terms, asOf, editingTermId })
  );
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<
    FullValidationErrorFragmentType[]
  >([]);

  const [mutate, { loading: saving }] = useMutation(
    SAVE_WORKER_PROFILE_CHANGE_MUTATION,
    {
      // Refresh the read views so the profile/Upcoming Changes reflect the change.
      refetchQueries: ['LoadBalancingWorker', 'ScheduleReview'],
      // errorPolicy is 'all', so GraphQL errors resolve here (not reject) with no worker.
      // Show inline validation errors, a toast for transport/GraphQL failures, and success
      // only when a worker actually came back.
      onCompleted: (data) => {
        const result = data?.saveWorkerProfileChange;
        const errors = result?.errors ?? [];
        if (errors.length > 0) {
          setServerErrors(errors);
          return;
        }
        if (!result?.worker) {
          createMessage(
            'error',
            'Could not save the profile change. Please try again.'
          );
          return;
        }
        createMessage('success', 'Profile change saved.');
        onSaved();
      },
      onError: () =>
        createMessage(
          'error',
          'Could not save the profile change. Please try again.'
        ),
    }
  );

  function update(patch: Partial<WorkerProfileFormValues>) {
    setValues((prev) => ({ ...prev, ...patch }));
  }

  function persist() {
    mutate({
      variables: buildSaveVariables({
        values,
        workerId,
        editingTermId,
        schedulingPeriodId,
      }),
    });
  }

  function save() {
    setSaveAttempted(true);
    // Effective Date is required and cannot be in the past.
    if (!values.effectiveDate || isPast(values.effectiveDate)) return;
    // End Date, when set, cannot fall before the effective date.
    if (isEndBeforeStart(values.effectiveDate, values.endDate)) return;

    // An ongoing (no end date) change supersedes everything after it; warn before removing
    // any later scheduled changes (only when creating).
    const replaced =
      !editingTermId && !values.endDate
        ? laterChanges(terms, values.effectiveDate)
        : [];
    if (replaced.length > 0) {
      confirmReplaceFutureChanges(values.effectiveDate, persist);
      return;
    }

    persist();
  }

  return { values, update, saveAttempted, serverErrors, saving, save };
}

// Warns that an ongoing change with no end date will remove the worker's later scheduled
// changes; runs the supplied save on confirm. Copy approved by design (Vicky Pan).
function confirmReplaceFutureChanges(
  effectiveDate: string,
  onConfirm: () => void
) {
  Modal.confirm({
    title: 'Replace future scheduled changes?',
    content: (
      <Gap vertical size="sm" align="stretch">
        <Text>
          {`This change has no end date and will become the worker's profile from ${datetime(
            effectiveDate
          ).format(
            'MMM D, YYYY'
          )} onward. Any scheduled changes after that date will be removed.`}
        </Text>
        <Text>
          To keep future scheduled changes, add an end date to make this a
          temporary change.
        </Text>
      </Gap>
    ),
    okText: 'Confirm',
    onOk: onConfirm,
  });
}
